import { CartItem } from '@pegasus/cart';

import { Campaign } from '@shared/types/mylounge';
import {
    NON_APPLICABLE,
    TRACK_RECO_NO_RECO,
    TRACK_TRAFFIC_SOURCES,
} from '@client/config/constants';
import { GridBlock } from '@client/config/layout';

import { tracker } from '@browsing/tracking';
import { getCurrencyCode } from './index';

// analytics, push into dataLayer:
export function track(type: string, payload?: string | Record<string, unknown>) {
    const trackingInfo = { event: type };

    if (typeof payload === 'object') {
        Object.assign(trackingInfo, payload);
    }
    if (typeof payload === 'string') {
        Object.assign(trackingInfo, { data: payload });
    }

    // redux analytics expects a type param, but gtm expects an campaign param, so rename it.
    return window.dataLayer && window.dataLayer.push(trackingInfo);
}

export const getTrackingForTrafficSource = (trafficSource: string) => {
    if (trafficSource && TRACK_TRAFFIC_SOURCES.includes(trafficSource)) {
        return { component: trafficSource };
    }

    return {};
};

function constructBannerDataObject({
    banner,
    position,
}: {
    banner: Partial<GridBlock>;
    position: number;
}) {
    return {
        id: banner.id,
        name: banner.id,
        position: `slot${position}`,
        creative: banner.id === 'newsletter_resignup' ? 'banner1' : 'banner2',
    };
}

const isEarlyAccess = (article: Record<string, unknown>) => (article.plusEarlyAccess ? '1' : '0');

// TODO: review the tracking logic here
// in articles we passing TrackingImpression[]
export function getRecoImpressionData(
    articles: Array<Record<string, unknown>>,
    appdomain: number,
    recoSource?: number,
) {
    const products = articles.map(article => {
        return {
            name: article.nameCategoryTag || NON_APPLICABLE,
            id: article.sku,
            price: article.specialPrice ? Number(article.specialPrice) / 100 : 0,
            brand: article.brand,
            category: article.silhouette ? article.silhouette : NON_APPLICABLE,
            variant: article.nameColor ? article.nameColor : NON_APPLICABLE,
            list: 'reco', // as requested by BI for just reco becasue they comes with no campaignId
            position: article.position ? article.position : 1,
            dimension1: NON_APPLICABLE,
            dimension2: (article.gender && article.gender.toString()) || NON_APPLICABLE,
            dimension3: article.brandCode ? article.brandCode : NON_APPLICABLE,
            dimension4: article.campaignIdentifier,
            dimension5: recoSource || article.source || TRACK_RECO_NO_RECO, // PDP(collaborative or top seller) OR Campaign Overview(mylounge), Default to no source
            dimension54: article.brand ? article.brand : NON_APPLICABLE,
            dimension99: isEarlyAccess(article),
        };
    });

    const trackingData = {
        ecommerce: {
            currencyCode: getCurrencyCode(appdomain),
            impressions: products,
        },
    };

    return trackingData;
}

export function getTrackingDataRemoveFromCart(cartItem: CartItem) {
    const gender = Array.isArray(cartItem.gender) ? cartItem.gender.join(',') : cartItem.gender;

    return {
        ecommerce: {
            remove: {
                products: [
                    {
                        name: cartItem.name,
                        id: cartItem.configSku,
                        price: cartItem.price / 100,
                        brand: cartItem.brand,
                        category: cartItem.nameCategoryTag,
                        variant: cartItem.nameColor,
                        quantity: 1,
                        dimension1: cartItem.size,
                        dimension2: gender || 'N/A',
                        dimension3: cartItem.brandCode,
                        dimension4: cartItem.campaignIdentifier,
                        dimension5: cartItem.reco,
                        dimension54: 'N/A',
                        dimension99: isEarlyAccess(cartItem as unknown as Record<string, unknown>),
                    },
                ],
            },
        },
    };
}

// TODO: review the tracking logic here
// in article we passing RecommendationLegacy
export function getTrackingDataProductClick(
    article: Record<string, unknown>,
    position: number,
    recoSource = 0,
    specialPrice: number,
) {
    const trackingData = {
        ecommerce: {
            click: {
                actionField: { list: article.brand },
                products: [
                    {
                        name: article.nameCategoryTag ? article.nameCategoryTag : 'N/A',
                        id: article.sku,
                        price: article.specialPrice
                            ? Number(article.specialPrice) / 100
                            : specialPrice / 100,
                        brand: article.brand,
                        category: article.nameCategoryTag ? article.nameCategoryTag : 'N/A',
                        variant: article.nameColor ? article.nameColor : 'N/A',
                        position,
                        dimension1: 'N/A',
                        dimension2: (article.gender && article.gender.toString()) || 'N/A',
                        dimension3: article.brandCode ? article.brandCode : 'N/A',
                        dimension4: article.campaignIdentifier,
                        dimension5: recoSource,
                        dimension54: article.brand,
                        dimension99: isEarlyAccess(article),
                    },
                ],
            },
        },
    };

    return trackingData;
}

export function getBannerImpressionData(data: { banner: GridBlock; position: number }) {
    const promotion = constructBannerDataObject(data);

    const trackingData = {
        ecommerce: {
            promoView: {
                promotions: [promotion],
            },
        },
    };

    return trackingData;
}

export function getCampaignImpressionData(impressions: any[]) {
    const trackingData = {
        ecommerce: {
            promotions: [...impressions],
        },
    };

    return trackingData;
}

export function getBannerClickData(data: { banner: Partial<GridBlock>; position: number }) {
    const promotion = constructBannerDataObject(data);
    const trackingData = {
        ecommerce: {
            promoClick: {
                promotions: [promotion],
            },
        },
    };

    return trackingData;
}

export function trackOpenCampaignClick(
    campaign: Campaign,
    {
        categoryTabId,
        position,
        visitorID,
        ...props
    }: {
        categoryTabId: string;
        component?: string;
        position: string;
        visitorID: string;
    },
) {
    tracker.track('campaignOverview_campaign_click', {
        productCampaign: campaign.campaignId,
        positionNumber: position,
        campaign_level_1: campaign.name,
        campaign_level_2: categoryTabId,
        visitorID,
        isEarlyAccess: isEarlyAccess(campaign as unknown as Record<string, unknown>),
        isGetInspired: '0',
        ...props,
    });
}
